/**
 * Onboarding flow schemas. PRD-003 PR-06, PR-52, PR-07, DATA-02, DATA-18.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * api-schemas FREEZE DEVIATION (flagged loudly per the impl v9 brief)
 * ─────────────────────────────────────────────────────────────────────────────
 * `@studio/api-schemas` is normally frozen. This module is added because the
 * onboarding draft/submit wire payloads and the welcome-view block (OQ-15) had
 * no shared schema. Nothing here mutates an existing schema: it composes the
 * already-shipped `contextDocSectionsPayloadSchema` (context-doc.ts) and adds
 * three new payloads. The barrel (index.ts) gains one `export *` line. No
 * existing export changes shape, so this is additive and TECH-26 schema-parity
 * safe (no DB column shape is implied that 0002's onboarding_state /
 * context_docs do not already have).
 *
 * Coordination note: Phase G consumes `welcomeViewBlockSchema` for the
 * /portal/welcome render (T-038). Keep the field names stable.
 */

import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import {
  companySectionSchema,
  audiencesSectionSchema,
  strategicContextSectionSchema,
  voiceConstraintsSectionSchema,
  initialPrioritiesSectionSchema,
  contextDocSectionsPayloadSchema,
} from './context-doc.js';

/**
 * The six-step onboarding flow (PR-06 + PR-52). Step 3 is the non-interactive
 * intermission and collects no data, so it is not a data step; the four data
 * steps map 1:1 onto the first four context-doc sections plus the fifth
 * (initial priorities) on step 6.
 *
 *   Step 1  factual     -> company
 *   Step 2  factual     -> audiences
 *   Step 3  intermission-> (no data — PR-52 locked copy)
 *   Step 4  reflective  -> strategic_context
 *   Step 5  reflective  -> voice_and_constraints
 *   Step 6  forward     -> initial_priorities
 */
export const ONBOARDING_STEP_COUNT = 6 as const;

export const onboardingStepSchema = z
  .number()
  .int()
  .min(1)
  .max(ONBOARDING_STEP_COUNT);
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

/**
 * The four data-bearing context-doc sections collected during onboarding,
 * plus initial_priorities. Reuses the canonical section schemas so the
 * onboarding write and the eventual context-doc read never drift.
 *
 * Every section is optional: the flow autosaves partial state on each step
 * (T-032 AC) and a subscriber can submit with sections left blank.
 */
export const onboardingSectionsSchema = z.object({
  company: companySectionSchema.optional(),
  audiences: audiencesSectionSchema.optional(),
  strategic_context: strategicContextSectionSchema.optional(),
  voice_and_constraints: voiceConstraintsSectionSchema.optional(),
  initial_priorities: initialPrioritiesSectionSchema.optional(),
});
export type OnboardingSections = z.infer<typeof onboardingSectionsSchema>;

/**
 * Draft autosave payload (T-032 AC: "Auto-save partial state into
 * context_docs.sections under draft"). The client sends the cumulative
 * sections it has so far plus the furthest step reached so a returning
 * subscriber resumes where they left off.
 */
export const onboardingDraftSchema = z.object({
  sections: onboardingSectionsSchema,
  furthest_step: onboardingStepSchema,
});
export type OnboardingDraft = z.infer<typeof onboardingDraftSchema>;

/**
 * Final submit payload (T-033 AC). Same shape as the draft sections; the
 * server-side write finalizes them into context_docs.sections, flips
 * onboarding_state.onboarding_status to 'submitted', and notifies Eric.
 *
 * It is intentionally NOT a superset of the draft: submission carries the
 * complete sections and no step pointer (the flow is over).
 */
export const onboardingSubmitSchema = z.object({
  sections: onboardingSectionsSchema,
});
export type OnboardingSubmit = z.infer<typeof onboardingSubmitSchema>;

/**
 * Welcome-view "What I noticed in your context" block (PR-07, DES-12, OQ-15).
 *
 * OQ-15 is OPEN: single freeform paragraph vs. a 3-bullet
 * (observation / opportunity / next step) structure. Interpretation taken for
 * V1 (documented in IMPL-NOTES, flagged for the validator/Eric):
 *
 *   A lightly-structured default. `body` is the freeform Eric-composed
 *   paragraph (the always-required core, matching the DES-12 wireframe which
 *   shows one prose block). `highlights` is an OPTIONAL ordered list Eric may
 *   fill if he wants the 3-bullet shape; the renderer shows bullets only when
 *   present, otherwise just the paragraph. This satisfies both readings of
 *   OQ-15 without locking the prescriptiveness decision in code: Eric picks
 *   per subscriber at compose time, and the schema does not force either.
 *
 * `welcome_view_block` in onboarding_state stores the JSON of this object as
 * text (DATA-18 column is `text`); the operator surface serializes it and the
 * /portal/welcome render parses it. A plain string is still accepted on read
 * for forward/backward tolerance (see welcomeViewBlockFromStored).
 */
export const welcomeViewBlockSchema = z.object({
  body: z.string().min(1),
  highlights: z.array(z.string().min(1)).max(5).optional(),
});
export type WelcomeViewBlock = z.infer<typeof welcomeViewBlockSchema>;

/**
 * Tolerant reader for the stored `onboarding_state.welcome_view_block` text.
 * Accepts either the JSON-encoded {@link welcomeViewBlockSchema} or a bare
 * paragraph string (degraded path) so the welcome view always renders.
 */
export function welcomeViewBlockFromStored(
  stored: string | null | undefined,
): WelcomeViewBlock | null {
  if (stored == null) return null;
  const trimmed = stored.trim();
  if (trimmed.length === 0) return null;
  try {
    const parsed = welcomeViewBlockSchema.safeParse(JSON.parse(trimmed));
    if (parsed.success) return parsed.data;
  } catch {
    // Not JSON — fall through to the bare-string interpretation.
  }
  return { body: trimmed };
}

/**
 * Serialize a {@link WelcomeViewBlock} to the PLAIN TEXT stored in
 * `onboarding_state.welcome_view_block`.
 *
 * ── Cross-phase data contract (Phase F writes, Phase G reads) ──────────────
 * Phase G's shipped /portal welcome renderer (T-038) reads
 * `welcome_view_block` as a bare string and renders it directly with
 * `whitespace-pre-line`. To stay strictly inside file ownership (Phase F must
 * not touch Phase G's component) AND avoid showing the subscriber raw JSON,
 * Phase F stores PLAIN TEXT, not JSON: the freeform paragraph, followed by the
 * optional OQ-15 highlights as a blank-line-separated bulleted block. This is
 * also exactly DES-12a ("plain text ... reads as if Eric typed it") and the
 * DES-12 wireframe's single prose block. The structured object is still what
 * Eric composes in the operator form (OQ-15 stays Eric's per-subscriber call);
 * only the persisted representation is flattened. `welcomeViewBlockFromStored`
 * round-trips the bare-string case, so a future structured consumer still
 * gets a usable {body}.
 */
export function welcomeViewBlockToPlainText(block: WelcomeViewBlock): string {
  const parts: string[] = [block.body.trim()];
  if (block.highlights && block.highlights.length > 0) {
    parts.push(block.highlights.map((h) => `- ${h.trim()}`).join('\n'));
  }
  return parts.join('\n\n');
}

/**
 * Operator review action payload (T-035 AC). Eric composes the welcome-view
 * block and the plain-text email body in /eric/onboarding, then a single
 * action persists both and dispatches the email.
 */
export const onboardingReviewSchema = z.object({
  welcome_view_block: welcomeViewBlockSchema,
  welcome_email_body: z.string().min(1),
});
export type OnboardingReview = z.infer<typeof onboardingReviewSchema>;

/**
 * A row in the operator onboarding-review queue (T-035 AC: "/eric/onboarding
 * lists onboarding_state rows with status='submitted'"). The context doc is
 * read alongside so Eric composes against it on the same screen.
 */
export const onboardingQueueRowSchema = z.object({
  onboarding_state_id: z.string().uuid(),
  subscriber_id: z.string().uuid(),
  subscriber_email: z.string(),
  submitted_at: isoTimestampSchema.nullable(),
  sections: contextDocSectionsPayloadSchema,
});
export type OnboardingQueueRow = z.infer<typeof onboardingQueueRowSchema>;
